import { Link } from 'react-router-dom';
import { getAllCategories, categoryToSlug } from '../../../utils/blogUtils';

interface CategoryListProps {
  currentCategory?: string;
}

const CATEGORY_ICONS: Record<string, string> = {
  'Treinamentos': 'ri-graduation-cap-line',
  'Planejamento': 'ri-calendar-check-line',
  'Team Building': 'ri-team-line',
  'Casamentos': 'ri-heart-line',
  'Tendências': 'ri-line-chart-line',
  'Guia Prático': 'ri-tools-line',
  'Guia Completo': 'ri-book-open-line',
  'Tecnologia': 'ri-cpu-line',
};

export default function CategoryList({ currentCategory }: CategoryListProps) {
  const categories = getAllCategories();
  const activeSlug = currentCategory ? categoryToSlug(currentCategory) : '';

  return (
    <div className="rounded-2xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2.5 px-5 py-4 border-b border-gray-100 dark:border-gray-700">
        <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary/5 dark:bg-secondary/20">
          <i className="ri-price-tag-3-line text-secondary dark:text-gray-300 text-base"></i>
        </div>
        <div>
          <p className="font-heading font-bold text-sm text-secondary dark:text-white leading-tight">
            Categorias
          </p>
          <p className="font-body text-xs text-gray-400 dark:text-gray-500">
            Explore por tópico
          </p>
        </div>
      </div>

      {/* List */}
      <ul className="p-3 space-y-1">
        {categories.map(cat => {
          const isActive = cat.slug === activeSlug;
          const icon = CATEGORY_ICONS[cat.name] ?? 'ri-article-line';

          return (
            <li key={cat.slug}>
              <Link
                to={`/blog/categoria/${cat.slug}`}
                className={`
                  flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all cursor-pointer group
                  ${isActive
                    ? 'bg-primary/10 dark:bg-primary/15'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-700/40'
                  }
                `}
              >
                {/* Icon */}
                <div className={`
                  w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0 transition-colors
                  ${isActive
                    ? 'bg-primary/15 dark:bg-primary/20'
                    : 'bg-gray-100 dark:bg-gray-700 group-hover:bg-primary/10 dark:group-hover:bg-primary/10'
                  }
                `}>
                  <i className={`${icon} text-xs ${isActive ? 'text-primary' : 'text-gray-400 dark:text-gray-500 group-hover:text-primary'}`}></i>
                </div>

                {/* Label */}
                <span className={`
                  flex-1 font-body text-sm font-medium leading-none
                  ${isActive
                    ? 'text-primary'
                    : 'text-gray-600 dark:text-gray-300 group-hover:text-primary transition-colors'
                  }
                `}>
                  {cat.name}
                </span>

                {/* Count badge */}
                <span className={`
                  font-body text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0
                  ${isActive
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 group-hover:bg-primary/10 group-hover:text-primary transition-colors'
                  }
                `}>
                  {cat.count}
                </span>

                {/* Arrow on active */}
                {isActive && (
                  <div className="w-3 h-3 flex items-center justify-center flex-shrink-0">
                    <i className="ri-arrow-right-s-line text-primary text-xs"></i>
                  </div>
                )}
              </Link>
            </li>
          );
        })}
      </ul>

      {/* Footer */}
      <div className="px-5 py-3.5 border-t border-gray-50 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/50">
        <Link
          to="/blog"
          className="flex items-center justify-center gap-1.5 w-full font-body text-xs font-semibold text-gray-400 hover:text-primary transition-colors cursor-pointer whitespace-nowrap"
        >
          <div className="w-3 h-3 flex items-center justify-center">
            <i className="ri-apps-2-line text-xs"></i>
          </div>
          Ver todos os artigos
        </Link>
      </div>
    </div>
  );
}
